/**
 * Copyright Motili Inc., 2025 All Rights Reserved
 */

import { Suspense, useEffect } from 'react';
import { theme, LoadingSpinner } from '@motili/ui';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider as ReduxProvider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { APIProvider } from '@vis.gl/react-google-maps';
import {
    createHashRouter,
    createRoutesFromElements,
    RouterProvider,
    Routes,
    Route,
    useNavigate,
} from 'react-router';
import * as Sentry from '@sentry/react';
import { useAuth0 } from '@auth0/auth0-react';

import Config from 'Config';
import { TourLoader } from 'common/guide-tour';
import Alert from 'common/components/Alert';
import { useZenDeskChat } from 'common/utils/hooks/useZenDeskChat';
import {
    attachStoreForCypress,
    isCypressEnvironment,
} from 'common/utils/cypressDetection';
import {
    setAuth0Logout,
    setAuth0ClientId,
} from 'common/services/Auth0LogoutService';
import { useAuth0Config } from 'Auth0ProviderWithConfig';
import RefreshOverlay from 'common/components/RefreshOverlay';
import {
    isLoggedIn,
    clearAuthenticationCache,
} from 'common/services/LogoutService';
import reduxStore from 'common/stores/redux/store';
import Login from 'common/views/login/LoginContainer';
import ChangePassword from 'common/views/change-password/ChangePasswordContainer';
import ResetPassword from 'common/views/reset-password/ResetPasswordContainer';
import WorkRequestForm from 'common/views/forms/WorkRequestFormContainer';
import MobileRouter from 'common/MobileRouter';
import AuthenticatedRouter from 'common/AuthenticatedRouter';
import Auth0Login from 'common/views/login/Auth0Login';
import Auth0Navigate from 'common/views/login/Auth0Navigate';
import GlobalHistory from 'history';
import { UserTokenProvider } from 'useUserToken';

import './style/sass/index.scss';

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

// Specific routes are 'lifted' to utilize data-router features like useBlocker
const router = createHashRouter(
    createRoutesFromElements(<Route path='*' element={<Root />} />)
);

function Root() {
    const navigate = useNavigate();
    const { logout, isAuthenticated, isLoading } = useAuth0();
    const { clientId, isLocal: isS2Local } = useAuth0Config();

    useEffect(() => {
        setAuth0Logout(logout);
        setAuth0ClientId(clientId);
    }, [logout, clientId]);

    /**
     * Root level guard: logout user if auth0 is not authenticated but user has a token
     * This sits next to the checks in the login and navigate components to catch any edge cases where the user might be logged in without auth0 authentication
     */
    useEffect(() => {
        if (!isLoading && !isAuthenticated && isLoggedIn()) {
            clearAuthenticationCache();
            navigate('/login');
        }
    }, [isLoading, isAuthenticated, navigate]);

    if (isLoading || (!isAuthenticated && isLoggedIn())) {
        return <RefreshOverlay />;
    }

    return (
        <>
            <title>Motili</title>
            <TourLoader />
            <GlobalHistory />
            <SentryRoutes>
                {/* Register public routes */}
                <Route
                    path='/login'
                    element={isS2Local ? <Login /> : <Auth0Login />}
                />
                <Route path='/legacy-login' element={<Login />} />
                <Route path='/redirect' element={<Auth0Navigate />} />

                <Route path='/updatePassword' element={<ChangePassword />} />
                <Route path='/resetpassword' element={<ResetPassword />} />
                <Route
                    path='/request-service/:clientId/:uuid'
                    element={<WorkRequestForm />}
                />
                <Route path='/m' element={<MobileRouter />} />
                {/* All nested routes are maintained in AuthenticatedRouter */}
                <Route path='/*' element={<AuthenticatedRouter />} />
            </SentryRoutes>
            <Alert />
        </>
    );
}

function App() {
    const persistor = persistStore(reduxStore);
    useZenDeskChat();

    useEffect(() => {
        // Attach Redux store for Cypress tests using multiple detection methods
        if (isCypressEnvironment() && reduxStore && persistor) {
            attachStoreForCypress(reduxStore, persistor);
        }
    }, [persistor]);

    return (
        <Suspense
            fallback={
                <LoadingSpinner fullscreen color={theme.colors.bgMain} />
            }
        >
            <ReduxProvider store={reduxStore}>
                <PersistGate loading={null} persistor={persistor}>
                    <UserTokenProvider>
                        <ThemeProvider theme={theme}>
                            <APIProvider apiKey={Config.googleMapsApiKey} libraries={["marker"]}  >
                                <RouterProvider router={router} />
                            </APIProvider>
                        </ThemeProvider>
                    </UserTokenProvider>
                </PersistGate>
            </ReduxProvider>
        </Suspense>
    );
}

export default App;
